// src/components/AnalysisTab.jsx
// "Analysis" tab (city tenants only; renamed from "Prediction"): where the
// board's attention goes, by quality-of-life category, per quarter. For each
// category we compute (a) the share of meeting time spent on matching agenda
// items (from transcript timestamps in Meetings.json_data), (b) how many
// bills/measures were voted and how many passed (from Votings), and compare
// the selected quarter against the prior one to show the trend. Categories
// are assigned by keyword matching — deterministic and explainable, but
// approximate; the header says so.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { fetchAllRows } from '../utils/fetchAllRows';
import SubscribeGate from '../auth/SubscribeGate';
import BillImpact from './BillImpact';
import { CATEGORIES, OTHER, classify, votePassed, openInNewTab } from './billCategories';
import './AnalysisTab.css';

/* ── Helpers ── */
const tsToSeconds = (ts) => {
  if (!ts) return null;
  const parts = String(ts).split(':').map(Number);
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
};

const quarterKey = (isoDate) => {
  const [y, m] = String(isoDate).slice(0, 10).split('-').map(Number);
  if (!y || !m) return null;
  return `${y}-Q${Math.ceil(m / 3)}`;
};

const quarterLabel = (key) => {
  const [y, q] = key.split('-');
  return `${q} ${y}`;
};

const fmtMinutes = (seconds) => {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
};

const EMPTY_CAT = { seconds: 0, items: 0, billsVoted: 0, billsPassed: 0 };

// Sum several quarter buckets into one aggregate (used for year-to-date).
function mergeQuarters(quarters, keys) {
  const out = { totalSeconds: 0, meetings: new Set(), cats: new Map() };
  for (const k of keys) {
    const q = quarters.get(k);
    if (!q) continue;
    out.totalSeconds += q.totalSeconds;
    q.meetings.forEach((id) => out.meetings.add(id));
    for (const [id, c] of q.cats) {
      const e = out.cats.get(id) || { ...EMPTY_CAT };
      e.seconds += c.seconds;
      e.items += c.items;
      e.billsVoted += c.billsVoted;
      e.billsPassed += c.billsPassed;
      out.cats.set(id, e);
    }
  }
  return out;
}

// Shared row builder: current aggregate vs an optional comparison aggregate.
function buildRows(cur, prev) {
  const allCats = [...CATEGORIES, OTHER];
  let maxShare = 0;
  const rows = allCats.map((cat) => {
    const c = cur.cats.get(cat.id) || EMPTY_CAT;
    const share = cur.totalSeconds ? (c.seconds / cur.totalSeconds) * 100 : 0;
    const prevC = prev && prev.cats.get(cat.id);
    const prevShare = prev && prev.totalSeconds
      ? ((prevC ? prevC.seconds : 0) / prev.totalSeconds) * 100
      : null;
    if (share > maxShare) maxShare = share;
    if (prevShare != null && prevShare > maxShare) maxShare = prevShare;
    return {
      cat,
      share,
      prevShare,
      delta: prevShare != null ? share - prevShare : null,
      seconds: c.seconds,
      items: c.items,
      billsVoted: c.billsVoted,
      billsPassed: c.billsPassed,
    };
  });
  // Sort by current share desc, "Governance & Other" pinned last.
  rows.sort((a, b) => {
    if (a.cat.id === 'other') return 1;
    if (b.cat.id === 'other') return -1;
    return b.share - a.share;
  });
  return { rows, maxShare };
}

/* ── Shared chart: category rows with current + comparison bars ── */
function CategoryChart({ rows, maxShare, currentLabel, prevLabel, ariaLabel, onCategoryClick }) {
  return (
    <>
      <div className="prd-legend">
        <span className="prd-legend-item">
          <span className="prd-swatch prd-swatch-current" /> {currentLabel}
        </span>
        {prevLabel && (
          <span className="prd-legend-item">
            <span className="prd-swatch prd-swatch-prev" /> {prevLabel}
          </span>
        )}
      </div>

      <div className="prd-table" role="table" aria-label={ariaLabel}>
        <div className="prd-row prd-row-head" role="row">
          <span role="columnheader">Category</span>
          <span role="columnheader">Share of meeting time</span>
          <span role="columnheader" className="prd-num-head">Trend</span>
          <span
            role="columnheader"
            className="prd-num-head"
            title="Agenda items the board discussed. One agenda item can bundle many separate votes, so this rarely equals Bills voted."
          >
            Items discussed
          </span>
          <span role="columnheader" className="prd-num-head">Bills voted</span>
          <span role="columnheader" className="prd-num-head">Passed</span>
        </div>
        {rows.map((r) => (
          <div className="prd-row" role="row" key={r.cat.id}>
            {onCategoryClick && r.billsPassed > 0 ? (
              <button
                type="button"
                className="prd-cat prd-cat-link"
                role="cell"
                title={`${r.cat.hint} — click to see the ${r.billsPassed} passed bill${r.billsPassed === 1 ? '' : 's'}`}
                onClick={() => onCategoryClick(r.cat.id)}
              >
                {r.cat.label}
              </button>
            ) : (
              <span className="prd-cat" role="cell" title={r.cat.hint}>
                {r.cat.label}
              </span>
            )}
            <span className="prd-bars" role="cell">
              <span className="prd-bar-track">
                <span
                  className="prd-bar prd-bar-current"
                  style={{ width: `${maxShare ? (r.share / maxShare) * 100 : 0}%` }}
                />
                {r.prevShare != null && (
                  <span
                    className="prd-bar prd-bar-prev"
                    style={{ width: `${maxShare ? (r.prevShare / maxShare) * 100 : 0}%` }}
                  />
                )}
              </span>
              <span className="prd-share" title={fmtMinutes(r.seconds)}>
                {r.share.toFixed(1)}%
              </span>
            </span>
            <span className="prd-num prd-trend" role="cell">
              {r.delta == null ? (
                '—'
              ) : Math.abs(r.delta) < 0.05 ? (
                <span title="No meaningful change">→ 0.0</span>
              ) : r.delta > 0 ? (
                <span title={`Up ${r.delta.toFixed(1)} percentage points`}>
                  ▲ +{r.delta.toFixed(1)}
                </span>
              ) : (
                <span title={`Down ${Math.abs(r.delta).toFixed(1)} percentage points`}>
                  ▼ −{Math.abs(r.delta).toFixed(1)}
                </span>
              )}
            </span>
            <span
              className="prd-num"
              role="cell"
              title={r.items ? `${r.items} agenda item${r.items === 1 ? '' : 's'} discussed` : undefined}
            >
              {r.items || '—'}
            </span>
            <span className="prd-num" role="cell">{r.billsVoted || '—'}</span>
            <span className="prd-num" role="cell">{r.billsPassed || '—'}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ── Gated content ── */
function AnalysisContent({ districtId }) {
  // Category rows deep-link into the Bills Passed tab's matching section, in a
  // new tab so this Analysis view stays open in place.
  const goToBills = (catId) => {
    openInNewTab(`${window.location.pathname}?tab=billspassed#bp-${catId}`);
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [votings, setVotings] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const [meetingsRes, votingsRes] = await Promise.all([
        supabase
          .from('Meetings')
          .select('id, date, json_data')
          .eq('districtId', districtId)
          .order('date', { ascending: false })
          .limit(200),
        fetchAllRows(() =>
          supabase
            .from('Votings')
            .select('id, voteDate, category, issue, outcome')
            .eq('districtId', districtId)
            .order('id')
        ),
      ]);
      if (cancelled) return;
      if (meetingsRes.error) setError(meetingsRes.error);
      else {
        setMeetings(meetingsRes.data || []);
        setVotings(votingsRes.error ? [] : votingsRes.data || []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  /* Aggregate: quarter -> category -> { seconds, items, billsVoted, billsPassed } */
  const byQuarter = useMemo(() => {
    const quarters = new Map();
    const bucket = (qk, catId) => {
      let q = quarters.get(qk);
      if (!q) {
        q = { totalSeconds: 0, meetings: new Set(), cats: new Map() };
        quarters.set(qk, q);
      }
      let c = q.cats.get(catId);
      if (!c) {
        c = { seconds: 0, items: 0, billsVoted: 0, billsPassed: 0 };
        q.cats.set(catId, c);
      }
      return { q, c };
    };

    for (const m of meetings) {
      const qk = quarterKey(m.date);
      if (!qk) continue;
      let parsed = m.json_data;
      // json_data is stored as a JSON string inside jsonb — unwrap up to twice.
      for (let i = 0; i < 2 && typeof parsed === 'string'; i++) {
        try { parsed = JSON.parse(parsed); } catch { parsed = null; }
      }
      const items = parsed && Array.isArray(parsed.agenda_items) ? parsed.agenda_items : [];
      for (const item of items) {
        const start = tsToSeconds(item.timestamp_start);
        const end = tsToSeconds(item.timestamp_end);
        const dur = start != null && end != null && end > start ? end - start : 0;
        const summaryText = item.summary
          ? `${item.summary.context || ''} ${item.summary.impact || ''}`
          : '';
        const catId = classify(`${item.title || ''} ${summaryText}`);
        const { q, c } = bucket(qk, catId);
        q.totalSeconds += dur;
        q.meetings.add(m.id);
        c.seconds += dur;
        c.items += 1;
      }
    }

    for (const v of votings) {
      const qk = quarterKey(v.voteDate);
      if (!qk) continue;
      const catId = classify(`${v.category || ''} ${v.issue || ''}`, v.category);
      const { c } = bucket(qk, catId);
      c.billsVoted += 1;
      if (votePassed(v.outcome)) c.billsPassed += 1;
    }

    const keys = Array.from(quarters.keys()).sort().reverse();
    return { quarters, keys };
  }, [meetings, votings]);

  // Default to the newest quarter that has data.
  useEffect(() => {
    if (!selectedQuarter && byQuarter.keys.length) {
      setSelectedQuarter(byQuarter.keys[0]);
    }
  }, [byQuarter.keys, selectedQuarter]);

  const view = useMemo(() => {
    if (!selectedQuarter) return null;
    const q = byQuarter.quarters.get(selectedQuarter);
    if (!q) return null;
    const prevKey = byQuarter.keys[byQuarter.keys.indexOf(selectedQuarter) + 1] || null;
    const prev = prevKey ? byQuarter.quarters.get(prevKey) : null;
    const { rows, maxShare } = buildRows(q, prev);
    return { rows, maxShare, prevKey, meetingsCount: q.meetings.size, totalSeconds: q.totalSeconds };
  }, [selectedQuarter, byQuarter]);

  /* Year to date: every quarter of the selected quarter's year up to and
     including it, compared against the same span of the prior year (so the
     comparison stays apples-to-apples rather than full-year vs partial). */
  const ytdView = useMemo(() => {
    if (!selectedQuarter) return null;
    const [yStr, qStr] = selectedQuarter.split('-Q');
    const year = Number(yStr);
    const throughQ = Number(qStr);
    const keysFor = (y) =>
      byQuarter.keys.filter((k) => {
        const [ky, kq] = k.split('-Q').map(Number);
        return ky === y && kq <= throughQ;
      });
    const curKeys = keysFor(year);
    if (!curKeys.length) return null;
    const prevKeys = keysFor(year - 1);
    const cur = mergeQuarters(byQuarter.quarters, curKeys);
    const prev = prevKeys.length ? mergeQuarters(byQuarter.quarters, prevKeys) : null;
    const { rows, maxShare } = buildRows(cur, prev);
    return {
      rows,
      maxShare,
      year,
      throughQ,
      quartersCount: curKeys.length,
      meetingsCount: cur.meetings.size,
      totalSeconds: cur.totalSeconds,
      prevYear: prev ? year - 1 : null,
      billsVoted: rows.reduce((s, r) => s + r.billsVoted, 0),
      billsPassed: rows.reduce((s, r) => s + r.billsPassed, 0),
    };
  }, [selectedQuarter, byQuarter]);

  if (loading) {
    return <div className="prd-card prd-status">Loading meeting analysis…</div>;
  }
  if (error) {
    return <div className="prd-card prd-status prd-status-error">Error: {error.message}</div>;
  }
  if (!view || !byQuarter.keys.length) {
    return (
      <div className="prd-card prd-empty">
        No processed meetings yet for this district. This view appears after
        meetings are analyzed.
      </div>
    );
  }

  return (
    <>
      {/* Page header: describes the whole tab. The quarter picker deliberately
          lives in the Quarter section below rather than up here — at page level
          it read as though every section on the page were quarter-scoped, when
          only the first one is. */}
      <div className="prd-card prd-header-card">
        <h2 className="prd-title">Analysis — where the board&rsquo;s attention goes</h2>
        <p className="prd-subtitle">
          Meeting time, bills voted, and bills passed per quality-of-life
          category — by quarter, year to date, and by what the passed bills
          actually did.
        </p>
        <p className="prd-honesty-note">
          Categories are assigned by keyword matching on agenda items and vote
          records, and time comes from transcript timestamps — both are
          approximate. Quarters with few processed meetings are partial.
        </p>
      </div>

      <div className="prd-card">
        <div className="prd-header-row prd-section-head">
          <h3 className="prd-section-title prd-section-title-inline">
            Quarter — {quarterLabel(selectedQuarter)}
          </h3>
          <label className="prd-quarter-picker">
            <span>Quarter</span>
            <select
              value={selectedQuarter || ''}
              onChange={(e) => setSelectedQuarter(e.target.value)}
            >
              {byQuarter.keys.map((k) => (
                <option key={k} value={k}>{quarterLabel(k)}</option>
              ))}
            </select>
          </label>
        </div>
        <p className="prd-meta prd-meta-tight">
          {quarterLabel(selectedQuarter)}: {view.meetingsCount} meeting
          {view.meetingsCount === 1 ? '' : 's'} · {fmtMinutes(view.totalSeconds)} of
          agenda time analyzed
          {view.prevKey ? ` · compared with ${quarterLabel(view.prevKey)}` : ' · no prior quarter to compare yet'}
        </p>
        <CategoryChart
          rows={view.rows}
          maxShare={view.maxShare}
          currentLabel={quarterLabel(selectedQuarter)}
          prevLabel={view.prevKey ? quarterLabel(view.prevKey) : null}
          ariaLabel={`Attention by category, ${quarterLabel(selectedQuarter)}`}
          onCategoryClick={goToBills}
        />
        <p className="prd-footnote">
          Trend is the change in time share vs the prior quarter, in percentage
          points. “Items discussed” counts agenda items the board took up;
          “Bills voted” counts individual recorded votes, and “Passed” counts
          those where yes outnumbered no. The two vote columns come from the
          vote record rather than the agenda, so they rarely match the item
          count — a single agenda item can carry many votes (a consent calendar
          or a bundle such as “Items 47–68” is one discussion but dozens of
          votes), while a discussion that ended without a roll call has none.
        </p>
      </div>

      {ytdView && (
        <div className="prd-card">
          <h3 className="prd-section-title">
            Year to date — {ytdView.year} (through Q{ytdView.throughQ})
          </h3>
          <p className="prd-meta prd-meta-tight">
            {ytdView.quartersCount} quarter{ytdView.quartersCount === 1 ? '' : 's'} ·{' '}
            {ytdView.meetingsCount} meeting{ytdView.meetingsCount === 1 ? '' : 's'} ·{' '}
            {fmtMinutes(ytdView.totalSeconds)} of agenda time ·{' '}
            {ytdView.billsPassed} of {ytdView.billsVoted} bills passed
            {ytdView.prevYear
              ? ` · compared with ${ytdView.prevYear} through Q${ytdView.throughQ}`
              : ' · no prior year to compare yet'}
          </p>
          <CategoryChart
            rows={ytdView.rows}
            maxShare={ytdView.maxShare}
            currentLabel={`${ytdView.year} YTD`}
            prevLabel={ytdView.prevYear ? `${ytdView.prevYear} YTD` : null}
            ariaLabel={`Attention by category, ${ytdView.year} year to date`}
            onCategoryClick={goToBills}
          />
          <p className="prd-footnote">
            Year to date pools every analyzed quarter of {ytdView.year} through Q
            {ytdView.throughQ}
            {ytdView.prevYear
              ? `, against the same span of ${ytdView.prevYear} so the comparison covers equal ground.`
              : '. A prior-year comparison appears once an earlier year has analyzed meetings.'}
          </p>
        </div>
      )}

      {/* Impact of what actually passed: composition + geography */}
      <BillImpact
        votings={votings}
        categories={[...CATEGORIES, OTHER]}
        classifyCategory={classify}
        districtId={districtId}
      />
    </>
  );
}

/* ── Tab entry point: district lookup, then the subscription gate ── */
function AnalysisTab() {
  const { abbreviation } = useParams();

  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setDistrict(null);
      const { data, error: err } = await supabase
        .from('Districts')
        .select('id, premium_gate')
        .eq('abbreviation', abbreviation)
        .single();
      if (cancelled) return;
      if (err || !data) setError(err || { message: 'District not found' });
      else setDistrict(data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [abbreviation]);

  if (loading) {
    return (
      <div className="prd-tab">
        <div className="prd-card prd-status">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="prd-tab">
        <div className="prd-card prd-status prd-status-error">Error: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="prd-tab">
      <SubscribeGate premiumGate={district?.premium_gate} featureName="Analysis">
        <AnalysisContent districtId={district.id} />
      </SubscribeGate>
    </div>
  );
}

export default AnalysisTab;
